import type { FileSegment, ImageParser, ParseResult } from './Parser';
import { getWebpDescription } from './WebpDescriptions';
import { ExifUtils } from './ExifUtils';

export class WebpParser implements ImageParser {
  parse(data: Uint8Array): ParseResult {
    const segments: FileSegment[] = [];
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let i = 0;

    // RIFF Header
    if (i + 12 <= data.length) {
      const riff = this.readFourCC(data, i);
      if (riff === 'RIFF') {
        segments.push({
          index: segments.length,
          type: 'RIFF',
          name: 'RIFF Header',
          start: i,
          payloadStart: i + 12, // After RIFF(4) + Size(4) + WEBP(4)
          end: i + 12, // Just the header part? usually acts as container.
          // Actually, RIFF is the container. The whole file is inside the RIFF payload technically?
          // But for structure viewing, we usually show RIFF header as one item, then WEBP signature, then chunks.
          // Let's model it as: RIFF Header (0-12), then Chunks.
          // Wait, 'WEBP' is at offset 8.
          // Let's create a segment for the Container Header
          size: 12
        });
        i += 12;
      }
    }

    while (i < data.length) {
      if (i + 8 > data.length) break; // Incomplete chunk header

      const fourCC = this.readFourCC(data, i);
      const size = view.getUint32(i + 4, true); // Little endian
      const padding = size % 2;
      const totalSize = 8 + size + padding;

      // Note: 'WEBP' signature is technically part of RIFF payload but often treated as file id.
      // But standard WebP parser treats everything after 12 bytes as chunks?
      // Actually, standard says: RIFF header (12 bytes), then sequence of chunks.
      // The first "chunk" isn't WEBP. WEBP is part of RIFF header at offset 8.

      // Let's adjust the first segment logic above.
      // Offset 0: 'RIFF'
      // Offset 4: File Size
      // Offset 8: 'WEBP'
      // These 12 bytes are the header. My previous logic covered this.
      // Now at 12, we expect chunks.

      segments.push({
        index: segments.length,
        type: fourCC,
        name: fourCC,
        start: i,
        payloadStart: i + 8,
        end: i + totalSize,
        size: totalSize
      });

      i += totalSize;
    }

    let metadata: Record<string, string> = {};
    const exifChunk = segments.find(s => s.name === 'EXIF');
    if (exifChunk && exifChunk.payloadStart < data.length) {
      // EXIF Chunk payload
      const payload = data.slice(exifChunk.payloadStart, exifChunk.end);
      metadata = ExifUtils.parse(payload);
    }

    return {
      segments,
      format: 'WebP',
      metadata
    };
  }

  getDescription(name: string): string {
    return getWebpDescription(name);
  }

  private readFourCC(data: Uint8Array, offset: number): string {
    let s = '';
    for (let j = 0; j < 4; j++) {
      if (offset + j < data.length) {
        s += String.fromCharCode(data[offset + j]);
      }
    }
    return s;
  }
}
